import { showErrorAlert, showSuccessAlert } from 'actions';
import {
  deleteCohort,
  deleteCohortFailure,
  deleteCohortRequest,
  deleteCohortsuccess,
  downloadCohortContract,
  fetchCohorts,
  fetchCohortsFailure,
  fetchCohortsFulfill,
  fetchCohortsRequest,
  fetchCohortsSuccess,
  fetchCohortStats,
  fetchCohortStatsFailure,
  fetchCohortStatsSuccess,
  fetchSingleCohort,
  fetchSingleCohortFailure,
  fetchSingleCohortRequest,
  fetchSingleCohortSuccess,
  upsertCohort,
  upsertCohortFailure,
  upsertCohortRequest,
  upsertCohortSuccess,
} from 'actions/cohorts';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { uploadFileWithS3URL } from 'reducers/fileUploads';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain } from 'selectors/auth';
import { getNavParamsByResource } from 'selectors/nav';
import { API, ErrorObject, SagaReturn } from 'types';
import { reverse } from 'utils/urls';
import { hashFileIncremental } from './fileUploads';

function* fetchSingleCohortSaga(action: ReturnType<typeof fetchSingleCohort>): SagaReturn {
  const { cohortId } = action.payload;
  yield put(fetchSingleCohortRequest());

  const params = {
    ...action.payload,
    relationships: ['audit_logs', 'audit_logs.payload.user', 'users'],
  };

  const response = (yield call([LitLingoClient.resources.cohorts, 'retrieve'], cohortId, {
    params,
  })) as API.Response<API.Cohorts.Retrieve>;

  if (response.error != null) {
    yield put(fetchSingleCohortFailure(response.error));
  } else {
    yield put(fetchSingleCohortSuccess(response.data));
  }
}

function* fetchCohortsSaga(action: ReturnType<typeof fetchCohorts>): SagaReturn {
  const payloadParams = action?.payload || {};

  yield put(fetchCohortsRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.cohorts)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const params: { include_count: boolean; kind?: string } = {
    include_count: true,
    ...resourceParams,
    ...payloadParams,
  };

  const response = (yield call([LitLingoClient.resources.cohorts, 'list'], {
    params,
  })) as API.Response<API.Cohorts.List>;

  if (response.error != null) {
    yield put(fetchCohortsFailure(response.error));
  } else {
    yield put(fetchCohortsSuccess(response.data));
  }

  yield put(fetchCohortsFulfill());
}

function* upsertCohortsaga(action: ReturnType<typeof upsertCohort>): SagaReturn {
  const { payload } = action;
  const { cohort, redirectToImport } = payload;
  const { file, ...rest } = cohort;
  const params = {};

  yield put(upsertCohortRequest());

  try {
    let hashedFile: { fileHash: string; contentType: string } | null = null;
    let data = {};

    if (file) {
      hashedFile = (yield call(hashFileIncremental, file)) as {
        fileHash: string;
        contentType: string;
      };

      data = { ...rest, contract_file_hash: hashedFile?.fileHash };
    } else if (payload.action === 'delete') {
      data = { ...rest, contract_file_hash: '', contract_filename: '' };
    } else {
      data = rest;
    }

    const response = (yield call([LitLingoClient.resources.cohorts, 'upsert'], {
      params,
      data,
    })) as API.Response<API.Cohorts.Upsert>;

    if (response.error != null) {
      yield put(upsertCohortFailure(response.error));

      yield put(
        showErrorAlert("Something went wrong and the new cohort can't be saved at this time")
      );
    } else {
      if (response.data.contract_upload?.upload_url && file) {
        yield put(
          uploadFileWithS3URL({
            file,
            fileName: rest.config.contract_filename || '',
            uploadURL: response.data.contract_upload.upload_url,
          })
        );
      }

      yield put(upsertCohortSuccess(response.data));
      yield put(showSuccessAlert(`Statement has been created successfully`));

      if (redirectToImport) {
        const customerDomain =
          ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

        const path = reverse({
          routeName: 'customer-cohort-import',
          customerDomain,
          routeParams: {
            cohortId: response.data.uuid,
          },
        });
        yield put(push(path));
      }
    }
  } catch (error) {
    const errorObject = error as ErrorObject;
    yield put(
      upsertCohortFailure({
        message:
          typeof errorObject === 'object' && errorObject != null && 'message' in errorObject
            ? String((errorObject as { message?: string }).message)
            : 'Upload failed',
      })
    );
  }
}

function* deleteCohortsaga(action: ReturnType<typeof deleteCohort>): SagaReturn {
  const { uuid, redirect } = action.payload;

  yield put(deleteCohortRequest());

  const response = (yield call(
    [LitLingoClient.resources.cohorts, 'delete'],
    uuid
  )) as API.Response<API.Cohorts.Upsert>;

  if (response.error != null) {
    yield put(deleteCohortFailure(response.error));
  } else {
    yield put(deleteCohortsuccess({ uuid }));
    yield put(showSuccessAlert('Cohort deleted'));
    if (redirect) {
      const customerDomain =
        ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

      const path = reverse({
        routeName: 'customer-overview',
        customerDomain,
      });
      yield put(push(path));
    }
  }
}

function* downloadContractURLSaga(action: ReturnType<typeof downloadCohortContract>): SagaReturn {
  const { cohortId } = action.payload;

  const response = (yield call([LitLingoClient.resources.cohorts.extras, 'downloadURL'], {
    urlParams: { cohortId },
  })) as API.Response<API.CustomerContracts.DownloadURL>;

  if (response.error != null) {
    yield put(showErrorAlert('Something went wrong.'));
  } else {
    window.open(response.data.payload);
  }
}

function* fetchCohortStatsSaga(action: ReturnType<typeof fetchCohortStats>): SagaReturn {
  const { cohortId } = action.payload;

  const response = (yield call([LitLingoClient.resources.cohorts.extras, 'stats'], {
    urlParams: { cohortId },
  })) as API.Response<API.Cohorts.Stats>;

  if (response.error != null) {
    yield put(showErrorAlert('Something went wrong.'));
    yield put(fetchCohortStatsFailure(response.error));
  } else {
    yield put(fetchCohortStatsSuccess(response.data));
  }
}

function* CohortsSaga(): SagaReturn {
  yield takeLatest(fetchSingleCohort.toString(), fetchSingleCohortSaga);
  yield takeLatest(fetchCohorts.toString(), fetchCohortsSaga);
  yield takeLatest(upsertCohort.toString(), upsertCohortsaga);
  yield takeLatest(deleteCohort.toString(), deleteCohortsaga);
  yield takeLatest(downloadCohortContract.toString(), downloadContractURLSaga);
  yield takeLatest(fetchCohortStats.toString(), fetchCohortStatsSaga);
}

export default CohortsSaga;
